import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import { User, Lock, AlertCircle } from 'lucide-react';
import { parseErrorMessage } from '@/utils/errorHandler';

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.username, formData.password);
      navigate('/dashboard');
    } catch (error: any) {
      setError(parseErrorMessage(error, '登录失败，请检查用户名和密码'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-elevated">
          <Card.Header className="text-center pb-6">
            <div className="mx-auto w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center mb-4">
              <User className="h-6 w-6 text-white" />
            </div>
            <Card.Title className="text-2xl font-bold text-gray-900">
              数字出版部
            </Card.Title>
            <p className="text-gray-600 mt-2">请登录您的账户</p>
          </Card.Header>

          <Card.Content>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    name="username"
                    type="text"
                    label="用户名"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="pl-10"
                    placeholder="请输入用户名"
                    autoComplete="username"
                  />
                  <User className="absolute left-3 top-9 h-4 w-4 text-gray-400" />
                </div>

                <div className="relative">
                  <Input
                    name="password"
                    type="password"
                    label="密码"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="pl-10"
                    placeholder="请输入密码"
                    autoComplete="current-password"
                  />
                  <Lock className="absolute left-3 top-9 h-4 w-4 text-gray-400" />
                </div>
              </div>

              {error && (
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-md">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                loading={loading}
                className="w-full"
                size="lg"
              >
                登录
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                还没有账户？{' '}
                <Link to="/register" className="text-primary-600 hover:text-primary-500 font-medium">
                  立即注册
                </Link>
              </p>
            </div>

          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

export default Login;